// src/pages/languages/AddWritingToLanguage.jsx
// Routes:
//   languages/skills/:skillId/writing/create
//   languages/skills/:skillId/writing/:itemId/edit
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  getApiError,
  getAllSkillItems,
  languageWritingAPI,
} from "../../services/languageService";

const DIFFICULTIES = ["EASY", "MEDIUM", "HARD"];

const inputCls =
  "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

export default function AddWritingToLanguage() {
  const { skillId, itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(itemId);

  const [form, setForm] = useState({
    title: "",
    question_text: "",
    min_words: 50,
    max_words: 200,
    points: 10,
    difficulty: "MEDIUM",
    sample_answer: "",
    rubric: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const backToSkill = () => navigate(`/dashboard/languages/skills/${skillId}`);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;

    const hydrate = (item) =>
      setForm({
        title: item.title || "",
        question_text: item.question_text || "",
        min_words: item.min_words ?? 50,
        max_words: item.max_words ?? 200,
        points: item.points ?? 10,
        difficulty: item.difficulty || "MEDIUM",
        sample_answer: item.sample_answer || "",
        rubric: item.rubric || "",
      });

    (async () => {
      try {
        const st = location.state?.question;
        let item = st && String(st.id) === String(itemId) && st.type === "WRITING" ? st : null;
        if (!item) {
          const all = await getAllSkillItems(skillId);
          item = all.find((i) => i.type === "WRITING" && String(i.id) === String(itemId));
        }
        if (cancelled) return;
        if (!item) setError("السؤال غير موجود");
        else hydrate(item);
      } catch (e) {
        if (!cancelled) setError(getApiError(e, "فشل تحميل البيانات"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, skillId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const min = parseInt(form.min_words, 10);
    const max = parseInt(form.max_words, 10);
    if (!form.title.trim()) return setError("العنوان مطلوب");
    if (!form.question_text.trim()) return setError("نص السؤال مطلوب");
    if (!Number.isFinite(min) || !Number.isFinite(max)) return setError("حدد الحد الأدنى والأقصى للكلمات");
    if (max <= min) return setError("الحد الأقصى للكلمات يجب أن يكون أكبر من الحد الأدنى");

    const payload = {
      title: form.title.trim(),
      question_text: form.question_text,
      min_words: min,
      max_words: max,
      points: Number(form.points) || 10,
      difficulty: form.difficulty,
      sample_answer: form.sample_answer,
      rubric: form.rubric,
    };

    try {
      setSaving(true);
      if (isEdit) await languageWritingAPI.update(itemId, payload);
      else await languageWritingAPI.create({ ...payload, language_skill: Number(skillId) });
      backToSkill();
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "فشل الحفظ، حاول مرة أخرى"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
      <div>
        <button
          type="button"
          onClick={backToSkill}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowRight className="w-4 h-4" />
          رجوع للمهارة
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? "تعديل" : "إضافة"} سؤال Writing
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="card space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">العنوان</label>
          <input className={inputCls} dir="auto" value={form.title} onChange={(e) => set({ title: e.target.value })} />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">نص السؤال</label>
          <textarea
            className={`${inputCls} min-h-[110px]`}
            dir="auto"
            value={form.question_text}
            onChange={(e) => set({ question_text: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">أقل عدد كلمات</label>
            <input type="number" min={1} className={inputCls} value={form.min_words} onChange={(e) => set({ min_words: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">أكثر عدد كلمات</label>
            <input type="number" min={1} className={inputCls} value={form.max_words} onChange={(e) => set({ max_words: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">النقاط</label>
            <input type="number" min={1} className={inputCls} value={form.points} onChange={(e) => set({ points: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الصعوبة</label>
            <select className={inputCls} value={form.difficulty} onChange={(e) => set({ difficulty: e.target.value })}>
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">إجابة نموذجية (اختياري)</label>
          <textarea
            className={`${inputCls} min-h-[90px]`}
            dir="auto"
            value={form.sample_answer}
            onChange={(e) => set({ sample_answer: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">معايير التقييم Rubric (اختياري)</label>
          <textarea
            className={`${inputCls} min-h-[90px]`}
            dir="auto"
            value={form.rubric}
            onChange={(e) => set({ rubric: e.target.value })}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="px-5 py-2.5 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "جاري الحفظ..." : isEdit ? "حفظ التعديلات" : "حفظ"}
        </button>
        <button
          type="button"
          onClick={backToSkill}
          disabled={saving}
          className="px-5 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          إلغاء
        </button>
      </div>
    </form>
  );
}